<script setup>
import { ref, computed } from 'vue';
import { Mark } from '../calc.js';
import { useShared } from '../state.js';
import { formatTimeAgo, useNowTicker } from '../use_relative_time.js';
import RecomputeButton from './recompute_button.vue';
import UnitSelect from './unit_select.vue';

const { inputs, recompute } = useShared();

const isSorted = computed(() => {
  for (let i = 1; i < inputs.marks.length; i++) {
    if (inputs.marks[i].inM() < inputs.marks[i - 1].inM()) return false;
  }
  return true;
});

// Only the mark's own "updated 3m ago" label timestamp — state.js's
// auto-touch watcher handles the setup's sync timestamp on its own for
// every edit (including add/remove/sort below), so there's nothing else to
// do in any of these.
function touchMark(mark) {
  mark.updated_at = Date.now();
}

const now = useNowTicker();
function markUpdatedAgo(mark) {
  now.value; // re-evaluate as time passes, not just when updated_at changes
  return formatTimeAgo(mark.updated_at);
}

function addRow() {
  console.log('add mark');
  inputs.marks.push(new Mark(30, 'm', 2.0, 1, Date.now()));
}

function removeRow(index) {
  console.log(`remove mark ${index}`);
  inputs.marks.splice(index, 1);
}

function sortMarks() {
  // Compare actual physical distance, not the raw number — rows can mix
  // units (one in yards, another in meters), so sorting by .distance alone
  // could come out wrong.
  inputs.marks.sort((a, b) => a.inM() - b.inM());
}

function onSubmit() {
  console.log("input marks submit");
  recompute();
}

</script>

<template>
  <div class="centered">
    <div class="table-scroll-wrapper">
      <table>
        <thead>
          <tr>
            <th>Distance</th>
            <th>Unit</th>
            <th>Mark</th>
            <th>Sight Bar</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(mark, index) in inputs.marks" :key="index">
              <td>
                <input type="number"
                  class="input-measure input-distance"
                  v-model.number="mark.distance"
                  @input="touchMark(mark)"
                  @keyup.enter="onSubmit" />
              </td>
              <td>
                <UnitSelect v-model="mark.unit" full-width @update:modelValue="touchMark(mark)" />
              </td>
              <td>
                <div class="mark-cell">
                  <input type="number"
                    class="input-measure input-mark"
                    v-model.number="mark.mark"
                    @input="touchMark(mark)"
                    @keyup.enter="onSubmit" />
                  <span v-if="markUpdatedAgo(mark)" class="updated-ago">{{ markUpdatedAgo(mark) }}</span>
                </div>
              </td>
              <td>
                <input type="number"
                  class="input-measure input-sight-bar"
                  v-model.number="mark.sight_bar_position"
                  @input="touchMark(mark)"
                  @keyup.enter="onSubmit" />
              </td>
              <td>
                <button
                  @click="removeRow(index)"
                  class="btn-delete"
                  title="Remove row"
                  aria-label="Remove row"
                  :disabled="inputs.marks.length <= 1"
                >
                  ✕
                </button>
              </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div class="centered">

    <button @click="addRow()" class="btn-add">
      + Add Row
    </button>

    <button v-if="!isSorted" @click="sortMarks()" class="btn-copy" title="Sort rows by distance">
      Sort by Distance
    </button>

    <RecomputeButton />

  </div>

</template>

<style scoped>

/* Scroll horizontally instead of forcing the whole page (and this panel)
   wider than the viewport — the unit toggle plus four other columns don't
   fit a phone screen at their natural width. */
.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
}

th {
  background: var(--color-bg-alt);
  font-size: var(--font-size-sm);
  /* Let header text wrap instead of forcing the column as wide as e.g.
     "Sight Bar" needs on one line — the values themselves (a handful of
     digits) need much less room, and that's what should drive the width. */
  white-space: normal;
}

/* Tighter than the app-wide default (0.4rem 0.6rem) — this table has five
   columns of small values, and the padding was eating more width than the
   values themselves needed. */
th, td {
  padding: 0.25rem 0.2rem;
}

/* The Mark column is taller than its row siblings once its "updated N ago"
   line is showing — top-align every cell so that extra line grows
   downward under the Mark input instead of the default middle-alignment
   pushing the Mark input itself out of line with Distance/Unit/Sight Bar. */
td {
  vertical-align: top;
}

.input-measure {
  width: 100%;
  box-sizing: border-box;
}

.mark-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.updated-ago {
  font-size: 0.7rem;
  color: var(--color-text);
  opacity: 0.6;
  white-space: nowrap;
}

/* Capped so the column doesn't grow to the browser's default number-input
   width (much wider than any value here needs) — but roomier than the
   tightest-possible digit count, so it doesn't feel cramped on a laptop. */
.input-distance  { max-width: calc(7ch + 10px); }
.input-mark      { max-width: calc(9ch + 10px); }
.input-sight-bar { max-width: calc(6ch + 10px); }

:deep(.unit-option) {
  padding: var(--space-xs) 4px;
}

.btn-delete {
  padding: 0.2rem 0.35rem;
}

.centered {
  padding: var(--space-xs);
}

</style>
